import { Fragment, useCallback, useEffect, useState } from 'react';

import { Audio } from './Audio.js';
import { AppDownloads, InviteDialog } from './AppDownloads.js';
import { NumberGreetings } from './NumberGreetings.js';
import { TrunkForm } from './TrunkForm.js';
import { UserForm } from './UserForm.js';
import { TranscriptionProgress, type Progress } from './TranscriptionProgress.js';
import { Voicemail } from './Voicemail.js';
import {
  api,
  clearToken,
  isAuthenticated,
  login,
  type Call,
  type PhoneNumber,
  type Trunk,
  type User,
} from './api.js';
import {
  Brand,
  CallIcon,
  ConfirmDialog,
  EmptyState,
  NumberIcon,
  SkeletonTable,
  ToastProvider,
  TrunkIcon,
  UserIcon,
  useToast,
} from './ui.js';

type Tab = 'trunks' | 'numbers' | 'users' | 'calls' | 'voicemail' | 'audio' | 'app';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'trunks', label: 'Trunks' },
  { id: 'numbers', label: 'Numbers' },
  { id: 'users', label: 'Users' },
  { id: 'calls', label: 'Calls' },
  { id: 'voicemail', label: 'Voicemail' },
  { id: 'audio', label: 'Audio' },
  { id: 'app', label: 'App' },
];

export function App(): JSX.Element {
  return (
    <ToastProvider>
      <Console />
    </ToastProvider>
  );
}

function Console(): JSX.Element {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [tab, setTab] = useState<Tab>('trunks');
  const [unreadVm, setUnreadVm] = useState(0);

  useEffect(() => {
    if (!authed) return;

    const refresh = (): void => {
      api.voicemail(1).then((r) => setUnreadVm(r.unread)).catch(() => {});
    };
    refresh();

    const timer = setInterval(refresh, 60_000);
    return () => clearInterval(timer);
  }, [authed, tab]);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <div className="shell">
      <header>
        <Brand sub="Console" />

        <nav>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={tab === t.id ? 'active' : ''}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.id === 'voicemail' && unreadVm > 0 && (
                <span className="nav-count">{unreadVm > 99 ? '99+' : unreadVm}</span>
              )}
            </button>
          ))}
        </nav>

        <button
          type="button"
          className="secondary"
          onClick={() => {
            clearToken();
            setAuthed(false);
          }}
        >
          Sign out
        </button>
      </header>

      <main>
        {tab === 'trunks' && <Trunks />}
        {tab === 'numbers' && <Numbers />}
        {tab === 'users' && <Users />}
        {tab === 'calls' && <Calls />}
        {tab === 'voicemail' && <Voicemail />}
        {tab === 'audio' && <Audio />}
        {tab === 'app' && <AppDownloads />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Login({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login" onSubmit={(e) => void submit(e)}>
        <Brand sub="Console" />
        <p className="login-hint">Sign in with an administrator account.</p>

        <label>
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </label>
        <label>
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Trunks(): JSX.Element {
  const toast = useToast();
  const [trunks, setTrunks] = useState<Trunk[] | null>(null);
  const [editing, setEditing] = useState<Trunk | null | 'new'>(null);
  const [confirming, setConfirming] = useState<Trunk | null>(null);

  const load = useCallback((): void => {
    api.trunks().then(setTrunks).catch((e) => toast.error(String(e)));
  }, [toast]);

  useEffect(() => {
    load();
    // Registration state changes without us acting; poll so the console does
    // not show a stale "Registered" after a carrier drops us.
    const timer = setInterval(load, 15_000);
    return () => clearInterval(timer);
  }, [load]);

  if (editing) {
    return (
      <TrunkForm
        trunk={editing === 'new' ? null : editing}
        onDone={() => {
          setEditing(null);
          load();
          toast.push({ title: 'Trunk saved', tone: 'ok' });
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>Trunks</h2>
          <p className="section-sub">
            Carrier connections. Credentials are stored encrypted and pushed to the
            media server automatically.
          </p>
        </div>
        <div>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              void api.syncTrunks().then(() => {
                load();
                toast.push({ title: 'Gateways resynced', tone: 'ok' });
              })
            }
          >
            Resync
          </button>
          <button type="button" onClick={() => setEditing('new')}>
            Add trunk
          </button>
        </div>
      </div>

      {trunks === null ? (
        <SkeletonTable columns={[22, 30, 12, 12, 16]} />
      ) : trunks.length === 0 ? (
        <EmptyState
          icon={<TrunkIcon size={22} />}
          title="No trunks configured"
          body="A trunk is your connection to a carrier. Add one to start making and receiving calls."
          action={{ label: 'Add trunk', onClick: () => setEditing('new') }}
        />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Host</th><th>Transport</th>
              <th>Channels</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {trunks.map((t) => (
              <tr key={t.id}>
                <td><strong>{t.name}</strong></td>
                <td className="cell-mono">{t.sipHost}:{t.sipPort}</td>
                <td>{t.transport.toUpperCase()}</td>
                <td>{t.maxChannels}</td>
                <td>
                  <span
                    className={`badge ${
                      t.registration === 'REGED' ? 'ok' : t.enabled ? 'bad' : ''
                    }`}
                  >
                    {t.registration === 'REGED'
                      ? 'Registered'
                      : t.enabled
                        ? t.registration
                        : 'Disabled'}
                  </span>
                </td>
                <td className="row-actions">
                  <button type="button" className="link" onClick={() => setEditing(t)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="link danger"
                    onClick={() => setConfirming(t)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirming && (
        <ConfirmDialog
          title={`Delete ${confirming.name}?`}
          body="Calls over this trunk stop immediately and the stored carrier credentials are destroyed. This cannot be undone."
          confirmLabel="Delete trunk"
          destructive
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const name = confirming.name;
            void api.deleteTrunk(confirming.id).then(() => {
              setConfirming(null);
              load();
              toast.push({ title: `${name} deleted`, tone: 'ok' });
            });
          }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function Numbers(): JSX.Element {
  const toast = useToast();
  const [numbers, setNumbers] = useState<PhoneNumber[] | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [trunks, setTrunks] = useState<Trunk[]>([]);
  const [editingAudio, setEditingAudio] = useState<PhoneNumber | null>(null);
  const [confirming, setConfirming] = useState<PhoneNumber | null>(null);
  const [form, setForm] = useState({ e164: '', label: '', trunkId: '', destUserId: '' });

  const load = useCallback((): void => {
    void Promise.all([api.numbers(), api.users(), api.trunks()])
      .then(([n, u, t]) => {
        setNumbers(n);
        setUsers(u);
        setTrunks(t);
      })
      .catch((e) => toast.error(String(e)));
  }, [toast]);

  useEffect(load, [load]);

  async function add(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    try {
      await api.createNumber({
        e164: form.e164,
        label: form.label || null,
        trunkId: form.trunkId || null,
        destType: form.destUserId ? 'user' : 'hangup',
        destUserId: form.destUserId || null,
      });
      setForm({ e164: '', label: '', trunkId: '', destUserId: '' });
      load();
      toast.push({ title: 'Number added', tone: 'ok' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  if (editingAudio) {
    return (
      <NumberGreetings
        number={editingAudio}
        onDone={() => {
          load();
          toast.push({ title: 'Audio updated', tone: 'ok' });
        }}
        onCancel={() => setEditingAudio(null)}
      />
    );
  }

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>Numbers</h2>
          <p className="section-sub">
            Inbound numbers on your trunks. Who they ring is set per user under
            Users; the greeting callers hear is set here.
          </p>
        </div>
      </div>

      <form className="card inline-form" onSubmit={(e) => void add(e)}>
        <input
          value={form.e164}
          onChange={(e) => setForm({ ...form, e164: e.target.value })}
          placeholder="+44 20 7946 0958"
          required
        />
        <input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Label (optional)"
        />
        <select
          value={form.trunkId}
          onChange={(e) => setForm({ ...form, trunkId: e.target.value })}
        >
          <option value="">Any trunk</option>
          {trunks.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <select
          value={form.destUserId}
          onChange={(e) => setForm({ ...form, destUserId: e.target.value })}
        >
          <option value="">Route by permission</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>Ring {u.displayName}</option>
          ))}
        </select>
        <button type="submit">Add</button>
      </form>

      {numbers === null ? (
        <SkeletonTable columns={[22, 16, 14, 20, 14]} />
      ) : numbers.length === 0 ? (
        <EmptyState
          icon={<NumberIcon size={22} />}
          title="No numbers yet"
          body="Add the numbers your carrier has assigned to you, then grant users inbound permission on them."
        />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Number</th><th>Label</th><th>Trunk</th>
              <th>Rings</th><th>Audio</th><th>Ring time</th><th />
            </tr>
          </thead>
          <tbody>
            {numbers.map((n) => {
              const rings = users
                .filter((u) => u.numbers.some((x) => x.id === n.id && x.canReceive))
                .map((u) => u.displayName);

              return (
                <tr key={n.id}>
                  <td className="cell-mono"><strong>{n.e164}</strong></td>
                  <td>{n.label ?? <span className="cell-dim">—</span>}</td>
                  <td>{n.trunkName ?? <span className="cell-dim">Any</span>}</td>
                  <td>
                    {rings.length > 0 ? (
                      rings.join(', ')
                    ) : n.destUserName ? (
                      n.destUserName
                    ) : (
                      <span className="cell-dim">Nobody</span>
                    )}
                  </td>
                  <td>
                    {n.greetingName ? (
                      <span className="badge ok">Greeting</span>
                    ) : (
                      <span className="badge plain cell-dim">None</span>
                    )}
                  </td>
                  <td>{n.ringTimeoutSec}s</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="link"
                      onClick={() => setEditingAudio(n)}
                    >
                      Audio
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      onClick={() => setConfirming(n)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {confirming && (
        <ConfirmDialog
          title={`Delete ${confirming.e164}?`}
          body="Calls to this number will be rejected. Existing call history is kept."
          confirmLabel="Delete number"
          destructive
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            void api.deleteNumber(confirming.id).then(() => {
              setConfirming(null);
              load();
              toast.push({ title: 'Number deleted', tone: 'ok' });
            });
          }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function Users(): JSX.Element {
  const toast = useToast();
  const [users, setUsers] = useState<User[] | null>(null);
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [editing, setEditing] = useState<User | null | 'new'>(null);
  const [inviting, setInviting] = useState<User | null>(null);

  const load = useCallback((): void => {
    void Promise.all([api.users(), api.numbers()])
      .then(([u, n]) => {
        setUsers(u);
        setNumbers(n);
      })
      .catch((e) => toast.error(String(e)));
  }, [toast]);

  useEffect(load, [load]);

  if (editing) {
    return (
      <UserForm
        user={editing === 'new' ? null : editing}
        numbers={numbers}
        onDone={(password) => {
          setEditing(null);
          load();
          if (password) {
            // Kept until dismissed — auto-hiding a one-time password loses it.
            toast.push({
              title: 'User created — copy this password now',
              text: password,
              mono: true,
            });
          } else {
            toast.push({ title: 'User saved', tone: 'ok' });
          }
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>Users</h2>
          <p className="section-sub">
            Inbound permission means a number rings that person. Outbound means they
            may present it as caller ID. A user with no outbound number cannot dial out.
          </p>
        </div>
        <div>
          <button type="button" onClick={() => setEditing('new')}>Add user</button>
        </div>
      </div>

      {users === null ? (
        <SkeletonTable columns={[20, 8, 26, 14, 18]} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={<UserIcon size={22} />}
          title="No users yet"
          body="Add the people who will use the softphone. Each gets an extension and their own number permissions."
          action={{ label: 'Add user', onClick: () => setEditing('new') }}
        />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Ext</th><th>Email</th><th>Role</th>
              <th>Inbound</th><th>Outbound</th><th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.active ? '' : 'inactive'}>
                <td>
                  <strong>{u.displayName}</strong>
                  {!u.active && <span className="perm-label">inactive</span>}
                </td>
                <td className="cell-mono">{u.extension}</td>
                <td className="cell-dim">{u.email}</td>
                <td><span className="badge plain">{u.role}</span></td>
                <td className="cell-mono">
                  {u.numbers.filter((n) => n.canReceive).map((n) => n.e164).join(', ') || (
                    <span className="cell-dim">—</span>
                  )}
                </td>
                <td className="cell-mono">
                  {u.numbers
                    .filter((n) => n.canPresent)
                    .map((n) => (n.isDefault ? `${n.e164} ★` : n.e164))
                    .join(', ') || <span className="cell-dim">—</span>}
                </td>
                <td className="row-actions">
                  <button type="button" className="link" onClick={() => setEditing(u)}>
                    Edit
                  </button>
                  <button type="button" className="link" onClick={() => setInviting(u)}>
                    Send app
                  </button>
                  <button
                    type="button"
                    className="link"
                    onClick={() =>
                      void api.resetPassword(u.id).then((r) => {
                        if (r.temporaryPassword) {
                          toast.push({
                            title: `New password for ${u.email}`,
                            text: r.temporaryPassword,
                            mono: true,
                          });
                        }
                      })
                    }
                  >
                    Reset password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {inviting && <InviteDialog user={inviting} onClose={() => setInviting(null)} />}
    </section>
  );
}

// ---------------------------------------------------------------------------

function Calls(): JSX.Element {
  const toast = useToast();
  const [calls, setCalls] = useState<Call[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    api.calls(100).then((r) => setCalls(r.calls)).catch((e) => toast.error(String(e)));
  }, [toast]);

  useEffect(
    () => () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    },
    [audioUrl],
  );

  async function open(call: Call): Promise<void> {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);

    if (openId === call.id) {
      setOpenId(null);
      return;
    }

    setOpenId(call.id);
    setTranscript(null);

    const detail = await api.transcript(call.id).catch(() => null);
    setProgress(detail?.transcriptionProgress ?? null);
    setTranscript(detail?.transcript?.text ?? null);

    if (call.recordingId) {
      const url = await api.recordingAudio(call.id).catch(() => null);
      if (url) setAudioUrl(url);
    }
  }

  function formatDuration(seconds: number | null): string {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>Calls</h2>
          <p className="section-sub">
            Every call, with its recording and transcript. Access is audited.
          </p>
        </div>
      </div>

      {calls === null ? (
        <SkeletonTable rows={6} columns={[20, 12, 18, 18, 16, 10]} />
      ) : calls.length === 0 ? (
        <EmptyState
          icon={<CallIcon size={22} />}
          title="No calls yet"
          body="Once your trunk is connected and someone makes a call, it will appear here with its recording and transcript."
        />
      ) : (
        <table>
          <thead>
            <tr>
              <th>When</th><th>Direction</th><th>From</th>
              <th>To</th><th>Agent</th><th>Duration</th><th />
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <Fragment key={c.id}>
                <tr>
                  <td>{new Date(c.startedAt).toLocaleString()}</td>
                  <td><span className="badge plain">{c.direction}</span></td>
                  <td className="cell-mono">{c.fromNumber ?? '—'}</td>
                  <td className="cell-mono">{c.toNumber ?? '—'}</td>
                  <td>{c.userName ?? <span className="cell-dim">—</span>}</td>
                  <td>{formatDuration(c.durationSec)}</td>
                  <td className="row-actions">
                    <button type="button" className="link" onClick={() => void open(c)}>
                      {openId === c.id ? 'Close' : 'Open'}
                    </button>
                  </td>
                </tr>

                {openId === c.id && (
                  <tr className="detail">
                    <td colSpan={7}>
                      {audioUrl ? (
                        <audio controls src={audioUrl} />
                      ) : c.recordingId ? (
                        <p className="hint" style={{ margin: 0 }}>Loading recording…</p>
                      ) : (
                        <p className="hint" style={{ margin: 0 }}>No recording.</p>
                      )}
                      {progress && progress.status !== 'done' && (
                        <TranscriptionProgress progress={progress} />
                      )}
                      {transcript ? (
                        <pre>{transcript}</pre>
                      ) : progress && progress.status !== 'done' ? null : (
                        <p className="hint" style={{ margin: '10px 0 0' }}>
                          No transcript available.
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
